import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaEdit, FaCheck, FaTimes, FaUserPlus, FaUserShield, FaLock, FaUnlock } from 'react-icons/fa';

const Settings = () => {
    const { fetchSettings } = useTheme();
    const [activeTab, setActiveTab] = useState('general');
    const { t, i18n } = useTranslation();
    const [settings, setSettings] = useState({
        site_title: '',
        company_name: '',
        phone: '',
        email: '',
        address: '',
        primary_color: '#17a5b2',
        secondary_color: '#17a5b2' // İkincil rengi de uyumlu yapalım
    });
    const [files, setFiles] = useState({
        logo_light: null,
        logo_dark: null,
        favicon: null,
        login_image: null
    });

    const [translations, setTranslations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    // Her satır için ayrı loading state'i — kullanıcı hangi satırın kaydedildiğini görsün
    const [savingId, setSavingId] = useState(null);
    
    // Yönetici Yönetimi State'leri
    const [admins, setAdmins] = useState([]);
    const [adminForm, setAdminForm] = useState({ username: '', email: '', password: '', is_active: true });
    const [editingAdminId, setEditingAdminId] = useState(null);
    const [showAdminForm, setShowAdminForm] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [resSettings, resTrans] = await Promise.all([
                    axios.get('/settings'),
                    axios.get('/translations')
                ]);
                if (resSettings.data) {
                    setSettings(prev => ({ ...prev, ...resSettings.data }));
                }
                if (resTrans.data && Array.isArray(resTrans.data.list)) {
                    setTranslations(resTrans.data.list);
                }
            } catch (error) {
                console.error('Veriler yüklenemedi');
            }
        };
        loadData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFiles(prev => ({ ...prev, [e.target.name]: e.target.files[0] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            Object.keys(settings).forEach(key => {
                formData.append(key, settings[key]);
            });
            if (files.logo_light) formData.append('logo_light', files.logo_light);
            if (files.logo_dark) formData.append('logo_dark', files.logo_dark);
            if (files.favicon) formData.append('favicon', files.favicon);
            if (files.login_image) formData.append('login_image', files.login_image);

            await axios.post('/settings', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Ayarlar güncellendi.');
            fetchSettings();
        } catch (error) {
            toast.error('Güncelleme başarısız.');
        }
    };

    const handleTranslationChange = (id, lang, value) => {
        setTranslations(prev =>
            prev.map(t => t.id === id ? { ...t, [lang]: value } : t)
        );
    };

    const saveTranslation = async (id) => {
        // State'den güncel veriyi al — stale closure sorununu önler
        const t = translations.find(item => item.id === id);
        if (!t) return;

        // fr alanı boşsa kullanıcıyı uyar
        if (!t.fr || t.fr.trim() === '') {
            toast.warning('Fransızca çeviri boş bırakılamaz.');
            return;
        }

        try {
            setSavingId(id);
            await axios.put('/translations', {
                id: t.id,
                tr: t.tr,
                fr: t.fr
            });

            // Çevirileri anında güncelle
            const res = await axios.get('/translations');
            i18n.addResourceBundle('tr', 'translation', res.data.resources.tr.translation, true, true);
            i18n.addResourceBundle('fr', 'translation', res.data.resources.fr.translation, true, true);
            setTranslations(res.data.list); // Ayarlar sayfasındaki listeyi de güncelle

            toast.success(`"${t.key_name}" çevirisi kaydedildi.`);
        } catch (error) {
            console.error('Çeviri kayıt hatası:', error.response?.data || error.message);
            toast.error('Kayıt başarısız: ' + (error.response?.data?.message || 'Sunucu hatası'));
        } finally {
            setSavingId(null);
        }
    };

    const filteredTranslations = useMemo(() => {
        return translations.filter(t =>
            (t.key_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.tr || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.fr || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [translations, searchTerm]);

    const handleResetTranslations = async () => {
        if (!window.confirm('Tüm Türkçe metinler varsayılan değerlerle güncellenecek. Fransızca çevirileriniz korunacak. Devam edilsin mi?')) return;

        try {
            setLoading(true);
            await axios.post('/translations/reset');
            toast.success('Çeviriler yüklendi.');
            const resTrans = await axios.get('/translations');
            // i18n örneğini de anında güncelle
            i18n.addResourceBundle('tr', 'translation', resTrans.data.resources.tr.translation, true, true);
            i18n.addResourceBundle('fr', 'translation', resTrans.data.resources.fr.translation, true, true);
            setTranslations(resTrans.data.list);
        } catch (error) {
            toast.error('Yükleme başarısız.');
        } finally {
            setLoading(false);
        }
    };

    // --- YÖNETİCİ İŞLEMLERİ ---
    const fetchAdmins = async () => {
        try {
            const res = await axios.get('/users/admins');
            // Gelen verinin dizi olup olmadığını kontrol et
            if (Array.isArray(res.data)) {
                setAdmins(res.data);
            } else {
                setAdmins([]); // Dizi değilse boş dizi ata (Hata önleyici)
            }
        } catch (error) {
            console.error('Yöneticiler yüklenemedi');
            setAdmins([]); // Hata durumunda boş dizi ata
        }
    };

    useEffect(() => {
        if (activeTab === 'admins') {
            fetchAdmins();
        }
    }, [activeTab]);

    const handleAdminChange = (e) => {
        const { name, value, type, checked } = e.target;
        setAdminForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAdminSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingAdminId) {
                // Güncelleme
                const dataToSend = { ...adminForm };
                if (!dataToSend.password) delete dataToSend.password; // Şifre boşsa gönderme
                await axios.put(`/users/admins/${editingAdminId}`, dataToSend);
                toast.success('Yönetici güncellendi.');
            } else {
                // Yeni Ekleme
                await axios.post('/users/admins', adminForm);
                toast.success('Yönetici eklendi.');
            }
            resetAdminForm();
            fetchAdmins();
        } catch (error) {
            toast.error(error.response?.data?.message || 'İşlem başarısız.');
        }
    };

    const handleAdminDelete = async (id) => {
        if (window.confirm('Bu yöneticiyi silmek istediğinize emin misiniz?')) {
            try {
                await axios.delete(`/users/admins/${id}`);
                toast.success('Yönetici silindi.');
                fetchAdmins();
            } catch (error) {
                toast.error('Silme işlemi başarısız.');
            }
        }
    };

    const handleAdminStatus = async (id, currentStatus) => {
        try {
            await axios.put(`/users/admins/${id}/status`, { is_active: !currentStatus });
            toast.success('Durum güncellendi.');
            fetchAdmins();
        } catch (error) {
            toast.error('Durum güncelleme başarısız.');
        }
    };

    const startEditAdmin = (admin) => {
        setEditingAdminId(admin.id);
        setAdminForm({
            username: admin.username,
            email: admin.email,
            password: '', // Güvenlik için şifreyi boş getiriyoruz
            is_active: admin.is_active
        });
        setShowAdminForm(true);
    };

    const resetAdminForm = () => {
        setEditingAdminId(null);
        setAdminForm({ username: '', email: '', password: '', is_active: true });
        setShowAdminForm(false);
    };

    return (
        <AdminLayout>
            <h2>{t('settings.title')}</h2>

            <ul className="nav nav-tabs mt-4 mb-3">
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>{t('settings.general_tab')}</button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'translations' ? 'active' : ''}`} onClick={() => setActiveTab('translations')}>{t('settings.translations_tab')}</button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'admins' ? 'active' : ''}`} onClick={() => setActiveTab('admins')}>{t('settings.admins_tab')}</button>
                </li>
            </ul>

            <div className="card shadow-sm">
                <div className="card-body">
                    {activeTab === 'general' && (
                        <form onSubmit={handleSubmit}>
                            <h5 className="mb-3 text-primary border-bottom pb-2">{t('settings.general_info')}</h5>
                            <div className="row mb-4">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.site_title')}</label>
                                    <input type="text" className="form-control" name="site_title" value={settings.site_title} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.company_name')}</label>
                                    <input type="text" className="form-control" name="company_name" value={settings.company_name} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.phone')}</label>
                                    <input type="text" className="form-control" name="phone" value={settings.phone} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.email')}</label>
                                    <input type="email" className="form-control" name="email" value={settings.email} onChange={handleChange} />
                                </div>
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">{t('settings.address')}</label>
                                    <textarea className="form-control" rows="2" name="address" value={settings.address} onChange={handleChange}></textarea>
                                </div>
                            </div>

                            <h5 className="mb-3 text-primary border-bottom pb-2">{t('settings.appearance')}</h5>
                            <div className="row mb-4">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">{t('settings.logo_light')}</label>
                                    <input type="file" className="form-control" name="logo_light" onChange={handleFileChange} accept="image/*" />
                                    <small className="text-muted">{t('settings.logo_light_desc')}</small>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">{t('settings.logo_dark')}</label>
                                    <input type="file" className="form-control" name="logo_dark" onChange={handleFileChange} accept="image/*" />
                                    <small className="text-muted">{t('settings.logo_dark_desc')}</small>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">{t('settings.favicon')}</label>
                                    <input type="file" className="form-control" name="favicon" onChange={handleFileChange} accept="image/*" />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.login_image')}</label>
                                    <input type="file" className="form-control" name="login_image" onChange={handleFileChange} accept="image/*" />
                                    <small className="text-muted">{t('settings.login_image_desc')}</small>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.primary_color')}</label>
                                    <input type="color" className="form-control form-control-color w-100" name="primary_color" value={settings.primary_color} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('settings.secondary_color')}</label>
                                    <input type="color" className="form-control form-control-color w-100" name="secondary_color" value={settings.secondary_color} onChange={handleChange} />
                                </div>
                            </div>

                            <button type="submit" className="btn btn-primary w-100 py-2">{t('settings.save_btn')}</button>
                        </form>
                    )}

                    {activeTab === 'translations' && (
                        <div>
                            <div className="mb-3">
                                <div className="d-flex gap-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t('settings.search_translation')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <button className="btn btn-warning text-white" onClick={handleResetTranslations} disabled={loading}>
                                        {loading ? t('common.loading') : 'Verileri Yükle'}
                                    </button>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-bordered align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '20%' }}>Alan (Key)</th>
                                            <th style={{ width: '35%' }}>{t('settings.turkish')}</th>
                                            <th style={{ width: '35%' }}>{t('settings.french')}</th>
                                            <th style={{ width: '10%' }}>{t('settings.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTranslations.length > 0 ? (
                                            filteredTranslations.map(item => (
                                                <tr key={item.id}>
                                                    <td><small className="text-muted fw-bold" style={{ fontSize: '0.75rem' }}>{item.key_name}</small></td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control bg-light"
                                                            value={item.tr || ''}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={item.fr || ''}
                                                            onChange={(e) => handleTranslationChange(item.id, 'fr', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        {/* Kaydet butonuna id geçiriyoruz — stale closure önlemi */}
                                                        <button
                                                            className="btn btn-sm btn-success w-100"
                                                            onClick={() => saveTranslation(item.id)}
                                                            disabled={savingId === item.id}
                                                        >
                                                            {savingId === item.id ? '...' : t('settings.save')}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center p-4">
                                                    <p className="text-muted mb-0">Çeviri verisi bulunamadı. "Verileri Yükle" butonuna basarak varsayılanları yükleyebilirsiniz.</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'admins' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="text-primary m-0"><FaUserShield className="me-2" />{t('settings.admin_list_title')}</h5>
                                {!showAdminForm && (
                                    <button className="btn btn-success btn-sm" onClick={() => setShowAdminForm(true)}>
                                        <FaUserPlus className="me-2" /> {t('settings.add_admin_btn')}
                                    </button>
                                )}
                            </div>

                            {showAdminForm && (
                                <div className="card bg-light mb-4 border-0">
                                    <div className="card-body">
                                        <form onSubmit={handleAdminSubmit}>
                                            <div className="row g-3">
                                                <div className="col-md-3">
                                                    <label className="form-label small fw-bold">{t('settings.admin_username')}</label>
                                                    <input type="text" className="form-control" name="username" value={adminForm.username} onChange={handleAdminChange} required />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label small fw-bold">{t('settings.admin_email')}</label>
                                                    <input type="email" className="form-control" name="email" value={adminForm.email} onChange={handleAdminChange} required />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label small fw-bold">{t('settings.admin_password')}</label>
                                                    <input type="password" className="form-control" name="password" value={adminForm.password} onChange={handleAdminChange} placeholder={editingAdminId ? t('settings.password_hint') : ''} required={!editingAdminId} />
                                                </div>
                                                <div className="col-md-2 d-flex align-items-end">
                                                    <div className="form-check mb-2">
                                                        <input className="form-check-input" type="checkbox" id="adminActive" name="is_active" checked={adminForm.is_active} onChange={handleAdminChange} />
                                                        <label className="form-check-label" htmlFor="adminActive">{t('settings.admin_active')}</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-1 d-flex align-items-end gap-2">
                                                    <button type="submit" className="btn btn-primary w-100">{t('settings.save_admin')}</button>
                                                    <button type="button" className="btn btn-secondary" onClick={resetAdminForm}><FaTimes /></button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t('settings.admin_username')}</th>
                                            <th>{t('settings.admin_email')}</th>
                                            <th>{t('settings.admin_status')}</th>
                                            <th className="text-end">{t('settings.admin_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {admins.map(admin => (
                                            <tr key={admin.id}>
                                                <td>{admin.username}</td>
                                                <td>{admin.email}</td>
                                                <td>
                                                    {admin.is_active ? (
                                                        <span className="badge bg-success">{t('settings.admin_active')}</span>
                                                    ) : (
                                                        <span className="badge bg-danger">{t('settings.admin_passive')}</span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <div className="btn-group">
                                                        <button 
                                                            className={`btn btn-sm ${admin.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                            onClick={() => handleAdminStatus(admin.id, admin.is_active)}
                                                            title={admin.is_active ? t('settings.admin_passive') : t('settings.admin_active')}
                                                        >
                                                            {admin.is_active ? <FaLock /> : <FaUnlock />}
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => startEditAdmin(admin)}
                                                            title={t('dealers.edit')}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleAdminDelete(admin.id)}
                                                            title={t('dealers.delete')}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default Settings;